import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection } from 'lexical';
import { useEffect } from 'react';
import { getEditorCaretPosition } from '../../../mentions/get-editor-caret-rect';
import { resolveLexicalCaretAnchorDom } from '../resolve-lexical-caret-anchor';

type MentionsTriggerPluginProps = {
  onMentionsTrigger?: (position?: { x: number; y: number }) => void;
};

export function MentionsTriggerPlugin({
  onMentionsTrigger,
}: MentionsTriggerPluginProps) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '@') {
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            const anchorOffset = selection.anchor.offset;
            const textBeforeCursor = anchorNode
              .getTextContent()
              .slice(0, anchorOffset);

            if (anchorOffset === 0 || /\s$/.test(textBeforeCursor)) {
              event.preventDefault();

              const rootElement = editor.getRootElement();
              const lexicalAnchor = resolveLexicalCaretAnchorDom({ editor });
              const position = getEditorCaretPosition({
                editorRoot: rootElement,
                fallbackAnchorElement: lexicalAnchor?.anchorElement,
                fallbackOffset: lexicalAnchor?.textOffset,
              });
              onMentionsTrigger?.(position);
            }
          }
        });
      }
    };

    const removeListener = editor.registerRootListener(
      (rootElement, prevRootElement) => {
        if (rootElement) {
          rootElement.addEventListener('keydown', handleKeyDown);
        }
        if (prevRootElement) {
          prevRootElement.removeEventListener('keydown', handleKeyDown);
        }
      },
    );

    return () => {
      removeListener();
    };
  }, [editor, onMentionsTrigger]);

  return null;
}
